import toast from '@/components/Toast';
import { get } from 'lodash';

/** @deprecated Call `toast.success` from `@/components/Toast` — this is an alias for it. */
export function yieldSuccessNotification(message) {
  toast.success(message);
}
/** @deprecated Call `toast.info` from `@/components/Toast` — this is an alias for it. */
export function yieldInfoNotification(message) {
  toast.info(message);
}

/**
 * @deprecated For a literal call `toast.error` from `@/components/Toast`. For a failed
 * API response call `showApiErrorToast(error, fallback)` from `@/services/api` and
 * delete the hand-rolled extraction at the call site.
 */
export function yieldErrorNotification(message) {
  toast.error(message);
}

/**
 * @deprecated For a literal call `toast.warn` from `@/components/Toast`. When the text
 * comes out of a response, extract it with `getApiErrorMessages` from `@/services/api`
 * and call `toast.warn` yourself.
 */
export function yieldWarningNotification(message) {
  toast.warn(message);
}

/** @deprecated Unused. Its `detail[]` shape is covered by `showApiErrorToast`. */
export function notifyErrorFromDetail(err) {
  const errorDetail = get(err, 'response.data.detail');
  let message = 'Something went wrong';

  if (Array.isArray(errorDetail) && errorDetail.length > 0) {
    message = errorDetail[0]?.msg ?? message;
  } else if (typeof errorDetail === 'string') {
    message = errorDetail;
  }
  toast.error(message);
}

/**
 * @deprecated Use `showApiErrorToast(error)` from `@/services/api`, and note that the
 * prefix does not survive: when `response.data.error` is a string the replacement
 * recognises it and shows it alone, so "Failed to import data: x" becomes "x". A
 * migration that must keep the prefix reads the text with `getApiErrorMessages` and
 * raises its own `toast.error` — and needs two new i18n keys, since both strings
 * here are hardcoded English.
 */
export const toastResponseError = (response) => {
  const error = get(response, 'response.data.error');

  if (typeof error === 'string') {
    return toast.error(`Failed to import data: ${error}`);
  }

  return toast.error('Import failed');
};

/** @deprecated Internal to yieldValidationNotification; use `showApiErrorToast`. */
export function validationNotification(err) {
  if (err && err.message) {
    toast.error(err.message);
  }
  if (err && err.errors) {
    Object.keys(err.errors).forEach((errorKey) => toast.error(err.errors[errorKey]));
  }
}

/**
 * @deprecated Use `showApiErrorToast(error, fallback)` from `@/services/api`.
 *
 * Its `err && err.response` guard is correct and the replacement keeps it: a saga
 * catch block sees everything, and a TypeError's message must not reach a toast.
 *
 * Everything else about it is one saga's local decision that reached 73 call sites
 * by copy-paste. It is not a generator — the `yield` prefix came from where its
 * first author happened to be standing. Its 400/422 range was that saga's need and
 * not a policy: outside it — 404, 409, every 5xx, a network drop — the failure is
 * silent. That silence is the reason to migrate.
 */
export function yieldValidationNotification(err) {
  if (
    err &&
    err.response &&
    (err.response.status.toString() === process.env.REACT_APP_VALIDATION_FAILED_CODE ||
      err.response.status.toString() === process.env.REACT_APP_DUPLICATED_VALIDATION_FAILED_CODE)
  ) {
    validationNotification(err.response.data);
  }
}

/**
 * @deprecated Use `showApiErrorToast(error, fallback)` from `@/services/api`. Its
 * `path` option exists only because it cannot recognise the response shape on its
 * own — the replacement can, so there is nothing to configure.
 *
 * @returns {boolean} - return result if toast was defined and appears
 */
export function handleErrorResponseWithToast(errorResponse, options = {}) {
  const originOptions = {
    path: 'data.message', // default path to get error
    type: 'error',
    defaultMessage: null,
    ...options,
  };

  const { defaultMessage, type, path } = originOptions;

  if (errorResponse && errorResponse.response) {
    const { response } = errorResponse;
    const error = get(response, path) || get(response, 'data.errors'); // alternative path to get error
    // when error is existed
    if (error) {
      if (Array.isArray(error)) {
        Object.keys(error).forEach((errorKey) => toast[type](error[errorKey]));
      } else {
        toast[type](error);
      }
      return true;
    }
    // when error is not found by path and exist default message
    if (!error && defaultMessage) {
      toast[type](defaultMessage);
      return true;
    }
  }
  // when response is undefined but exist default message
  if (defaultMessage) {
    toast[type](defaultMessage);
    return true;
  }

  return false;
}
